import type {
  BrowserClient,
  BrowserConfig,
  Plugin,
} from '@amplitude/analytics-core';
import { datadogLogs } from '@datadog/browser-logs';

import {
  type AuthEventProperties,
  buildUserEventProperties,
  detectNullFields,
  type UserEventProperties,
} from '@cfa/pathway-fe-shared';

import { getHighestUserRole } from '@/utils/user';

import { OktaUserRaw } from '@/types';

import Constants from 'constants/index';

type ExecuteFunction = Plugin<BrowserClient, BrowserConfig>['execute'];

export const getWebUserEventProperties = (
  user: OktaUserRaw,
): UserEventProperties => {
  const rawFields = {
    userLocations: user['cfa-locations'],
    userPerms: user.cfa_perms,
    permissionLevel: getHighestUserRole(
      user.cfa_perms[Constants.APPLICATION_SYSTEM_TAG],
    ),
    isWebview: window.native.isNative,
  };

  const nullFields = detectNullFields(rawFields);
  if (nullFields.length > 0) {
    datadogLogs.logger.warn(
      'Amplitude identify received null fields from backend',
      { nullFields: nullFields.join(',') },
    );
  }

  return buildUserEventProperties(rawFields);
};

// Enrichment plugin guarantees user fields on EVERY event including defaultTracking
// page_views that fire during init() — before identify() has a chance to run.
export const addUserPropertiesPlugin = (
  user: OktaUserRaw,
  authReady: Promise<AuthEventProperties>,
  getLatestAuth: () => AuthEventProperties | null,
): Plugin<BrowserClient, BrowserConfig> => {
  const name = 'add-user-properties-plugin';
  const type = 'enrichment';

  const userProps = getWebUserEventProperties(user);

  let initialAuth: AuthEventProperties | null = null;
  authReady.then(props => {
    initialAuth = props;
  });

  const execute: ExecuteFunction = async event => {
    // Prefer latest auth (handles re-identify), fall back to initial resolved value
    const resolved = getLatestAuth() ?? initialAuth ?? (await authReady);
    event.event_properties = {
      ...event.event_properties,
      ...userProps,
      ...resolved,
    };
    return event;
  };

  return {
    name,
    type,
    execute,
  };
};
